import * as Sentry from '@sentry/react';
import type { FC, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Rendered instead of the map when it throws. Defaults to nothing. */
  fallback?: ReactNode;
}

// Sentry.ErrorBoundary is a real, working React error boundary, but referencing
// it in app code surfaces a duplicate-@types/react construct-signature mismatch
// (the app entry that also uses it, index.tsx, isn't in the tsc program, and
// skipLibCheck hides the node_modules copy). Cast past the bogus type error.
const SentryErrorBoundary = Sentry.ErrorBoundary as unknown as FC<{
  fallback?: ReactNode;
  children?: ReactNode;
}>;

/**
 * Contains crashes from the Google Maps library (@vis.gl/react-google-maps).
 *
 * AdvancedMarker throws during init when the Maps API key referrer isn't
 * authorized for the current origin (RefererNotAllowedMapError on localhost),
 * and occasionally on transient marker-init races. Without containment that
 * throw bubbles to the app-level ErrorBoundary and white-screens the whole
 * page — catastrophic on the property form, where it discards everything the
 * user typed. This boundary keeps the failure local to the map. It still
 * catches + shows the fallback when Sentry isn't initialized (local dev), and
 * reports in production where it is.
 */
export function MapErrorBoundary({ children, fallback }: Props) {
  return (
    <SentryErrorBoundary fallback={<>{fallback ?? null}</>}>
      {children}
    </SentryErrorBoundary>
  );
}
